import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import { PasslySelect, ModalActionsComponent, PasslyButton } from '@app/components';
import {
    getModalSwitch,
    getLoaderBtn,
    handleGetPoliciesAuthData,
    handleGetProvisioningPoliciesDTOData,
    handleImportUsers,
    getImportUsersError
} from '@app/store/actions';
import { fetchPoliciesSelectLimit, onPressEnterModal } from '@app/helpers';
import { IPolicyAuth, Order } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';

export interface IModalImportFormData {
    selectValue?: any[];
}

export default function ImportUsersModalForm(props: IModalImportFormData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const policiesData: IPolicyAuth[] = useSelector((state: any) => state.provisioningPolicies.provisioningPoliciesDTO);
    const importUsersErrorMessage = useSelector((state: any) => state.users.importUsersError);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);

    const [selectProvPolicyValue, setProvPolicySelectValue] = useState('');
    const [selectedFileName, setSelectedFileName] = useState(translation('NEW_USER_BULK_FILE_NOT_SELECTED'));
    const [inputFile, setInputFile] = useState<object>({});
    const [buttonState, setButtonState] = useState(true);
    const [csvImportRef, setCsvImportRef] = useState<any>();

    useEffect(() => {
        dispatch(handleGetProvisioningPoliciesDTOData());
    }, []);

    useEffect(() => {
        dispatch(handleGetPoliciesAuthData('DisplayName', Order.desc, fetchPoliciesSelectLimit, 0, false));
    }, [dispatch]);

    useEffect(() => {
        if (policiesData && policiesData.length > 0) {
            setProvPolicySelectValue(policiesData[0].Id);
        }
    }, [policiesData]);

    const handleProvPolicySelectValue = (e: any) => {
        setProvPolicySelectValue(e.target.value);
    };

    const handleDownloadSampleFile = () => {
        const link = document.createElement('a');
        link.href = '/assets/samples/sampleimport.csv';
        if (link.download !== undefined) {
            const fileName = 'sampleimport.csv';
            link.download = fileName;
        }

        if (document.createEvent) {
            const e = document.createEvent('MouseEvents');
            e.initEvent('click', true, true);
            link.dispatchEvent(e);
            return false;
        }
    };

    const handleImportUsersBtn = () => {
        dispatch(handleImportUsers(inputFile, organizationFilter, selectProvPolicyValue));
        dispatch(getLoaderBtn(true));
    };

    const handleCancel = () => {
        dispatch(getImportUsersError(false));
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
    };

    const handleInputValue = (e: any) => {
        if (e.target.files.length) {
            const formData = new FormData();
            formData.append('file', e.target.files[0]);

            setInputFile(formData);
            setSelectedFileName(e.target.files[0].name);
            setButtonState(false);
        } else {
            return;
        }
    };

    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleImportUsersBtn, buttonState)}>
                <div className='modal__body--row'>
                    <Box pb={3}>
                        <h5>
                            <span>{translation('IMPORT_STEP_ONE_HEADER')}</span>{' '}
                            <span className='modal__form-subheader'>{translation('IMPORT_STEP_ONE_MESSAGE')}</span>
                        </h5>
                        <p className='modal__form-details'>{translation('IMPORT_STEP_ONE_DETAILS')}</p>
                        <PasslyButton
                            variant='contained'
                            color='primary'
                            id='users-sample-csv-btn'
                            className='modal__form-btn'
                            onClick={handleDownloadSampleFile}
                            startIcon={<GetAppIcon />}
                        >
                            {translation('IMPORT_DOWNLOAD_SAMPLE')}
                        </PasslyButton>
                    </Box>
                </div>
                <div className='modal__body--row'>
                    <Box pb={3}>
                        <h5>
                            <span>{translation('IMPORT_STEP_TWO_HEADER')}</span>{' '}
                            <span className='modal__form-subheader'>{translation('IMPORT_STEP_TWO_MESSAGE')}</span>
                        </h5>
                        <label htmlFor='users-import-btn'>
                            <input
                                ref={(ref) => setCsvImportRef(ref)}
                                onChange={handleInputValue}
                                style={{ display: 'none' }}
                                id='users-import-btn'
                                type='file'
                            />
                            <PasslyButton
                                variant='contained'
                                color='primary'
                                className='modal__form-btn'
                                onClick={() => csvImportRef.click()}
                                startIcon={<PublishIcon />}
                            >
                                {translation('IMPORT_BUTTON')}
                            </PasslyButton>
                        </label>

                        <h5>
                            <span>{translation('IMPORT_FILE_HEADER')}</span>
                            <span className='modal__form-subheader'>{selectedFileName}</span>
                        </h5>
                    </Box>
                </div>
                <div className='modal__body--row'>
                    <Box pb={3}>
                        <PasslySelect
                            value={selectProvPolicyValue}
                            options={policiesData}
                            optionLabel='DisplayName'
                            optionValue='Id'
                            errorMessage={importUsersErrorMessage && translation('ADD_USERS_ERROR')}
                            onChange={handleProvPolicySelectValue}
                            id='select-input'
                            label={'PROVISIONING_POLICY'}
                            appendTo={document.body}
                        />
                    </Box>
                </div>
            </form>
            <ModalActionsComponent
                primaryBtn={translation('ADD_USERS')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleImportUsersBtn}
                onClickSecondary={handleCancel}
                disabled={buttonState}
                id='import'
            />
        </>
    );
}
